import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { ScrollToTop } from './components/ScrollToTop';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Critical: Load immediately for best UX
import { LandingPage } from './pages/LandingPage';

// Lazy load all other pages
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const Appointments = lazy(() => import('./pages/Appointments').then(m => ({ default: m.Appointments })));
const BookingPage = lazy(() => import('./pages/BookingPage').then(m => ({ default: m.BookingPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard').then(m => ({ default: m.CustomerDashboard })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const GalleryPage = lazy(() => import('./pages/GalleryPage').then(m => ({ default: m.GalleryPage })));
const LandingPageModern = lazy(() => import('./pages/LandingPageModern').then(m => ({ default: m.LandingPageModern })));
const LandingPageVariant1 = lazy(() => import('./pages/LandingPageVariant1').then(m => ({ default: m.LandingPageVariant1 })));
const LandingPageVariant2 = lazy(() => import('./pages/LandingPageVariant2').then(m => ({ default: m.LandingPageVariant2 })));
const LandingPageVariant3 = lazy(() => import('./pages/LandingPageVariant3').then(m => ({ default: m.LandingPageVariant3 })));
const DesignComparison = lazy(() => import('./pages/DesignComparison').then(m => ({ default: m.DesignComparison })));
const Login = lazy(() => import('./pages/Login'));
const MyVehicles = lazy(() => import('./pages/MyVehicles').then(m => ({ default: m.MyVehicles })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const ReplacementVehiclesPage = lazy(() => import('./pages/ReplacementVehiclesPage').then(m => ({ default: m.ReplacementVehiclesPage })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const TermsConditions = lazy(() => import('./pages/TermsConditions').then(m => ({ default: m.TermsConditions })));
const WorkOrderDetail = lazy(() => import('./pages/WorkOrderDetail').then(m => ({ default: m.WorkOrderDetail })));
const Register = lazy(() => import('./pages/Register'));
const WorkOrdersList = lazy(() => import('./pages/WorkOrdersList').then(m => ({ default: m.WorkOrdersList })));
const HeroComparison = lazy(() => import('./pages/HeroComparison').then(m => ({ default: m.HeroComparison })));
const PneuservisTrencinPage = lazy(() => import('./pages/services/PneuservisTrencinPage').then(m => ({ default: m.PneuservisTrencinPage })));
const GeometriaTrencinPage = lazy(() => import('./pages/services/GeometriaTrencinPage').then(m => ({ default: m.GeometriaTrencinPage })));
const AdasKalibraciaTrencinPage = lazy(() => import('./pages/services/AdasKalibraciaTrencinPage').then(m => ({ default: m.AdasKalibraciaTrencinPage })));
const DiagnostikaTrencinPage = lazy(() => import('./pages/services/DiagnostikaTrencinPage').then(m => ({ default: m.DiagnostikaTrencinPage })));
const DetailingTrencinPage = lazy(() => import('./pages/services/DetailingTrencinPage').then(m => ({ default: m.DetailingTrencinPage })));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white">Načítavam...</div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/hero-preview" element={<HeroComparison />} />
            
            {/* Design Comparison Routes */}
            <Route path="/design-comparison" element={<DesignComparison />} />
            <Route path="/design-modern" element={<LandingPageModern />} />
            <Route path="/design-variant-1" element={<LandingPageVariant1 />} />
            <Route path="/design-variant-2" element={<LandingPageVariant2 />} />
            <Route path="/design-variant-3" element={<LandingPageVariant3 />} />
            
            {/* Main Public Routes - All Slovak */}
            <Route path="/cennik" element={<PricingPage />} />
            <Route path="/pricing" element={<Navigate to="/cennik" replace />} />
            
            <Route path="/rezervacia" element={<BookingPage />} />
            <Route path="/booking" element={<Navigate to="/rezervacia" replace />} />
            
            <Route path="/nahradne-vozidla" element={<ReplacementVehiclesPage />} />
            <Route path="/replacement-vehicles" element={<Navigate to="/nahradne-vozidla" replace />} />
            
            <Route path="/o-nas" element={<AboutPage />} />
            <Route path="/about" element={<Navigate to="/o-nas" replace />} />
            
            <Route path="/ochrana-sukromia" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<Navigate to="/ochrana-sukromia" replace />} />
            
            <Route path="/obchodne-podmienky" element={<TermsConditions />} />
            <Route path="/terms" element={<Navigate to="/obchodne-podmienky" replace />} />
            
            <Route path="/galerija" element={<GalleryPage />} />
            <Route path="/gallery" element={<Navigate to="/galerija" replace />} />
            
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/contact" element={<Navigate to="/kontakt" replace />} />
            
            <Route path="/faq" element={<FAQPage />} />
            
            {/* Service Pages */}
            <Route path="/sluzby/pneuservis-trencin" element={<PneuservisTrencinPage />} />
            <Route path="/sluzby/geometria-trencin" element={<GeometriaTrencinPage />} />
            {/* Redirect old URL to new SEO-friendly URL */}
            <Route path="/sluzby/3d-geometria-trencin" element={<Navigate to="/sluzby/geometria-trencin" replace />} />
            <Route path="/sluzby/adas-kalibracia-trencin" element={<AdasKalibraciaTrencinPage />} />
            <Route path="/sluzby/adas" element={<Navigate to="/sluzby/adas-kalibracia-trencin" replace />} />
            <Route path="/sluzby/diagnostika-trencin" element={<DiagnostikaTrencinPage />} />
            <Route path="/sluzby/auto-detailing-trencin" element={<DetailingTrencinPage />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/vehicles"
              element={
                <ProtectedRoute>
                  <MyVehicles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/appointments"
              element={
                <ProtectedRoute>
                  <Appointments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/work-orders"
              element={
                <ProtectedRoute>
                  <WorkOrdersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/work-orders/:id"
              element={
                <ProtectedRoute>
                  <WorkOrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
